<template>
    <b-modal :id="modalId" title="테스트 발송" size="lg" hide-footer @hidden="resetForm">
        <div v-if="loadingVars" class="text-center py-4">
            <b-spinner variant="primary" />
            <p class="mt-2 text-muted small">템플릿을 준비하는 중...</p>
        </div>

        <div v-else>
            <!-- 수신자 이메일 -->
            <b-form-group label="수신자 이메일 *" label-class="small font-weight-bold">
                <b-form-input
                    v-model="form.recipient"
                    type="email"
                    placeholder="test@example.com"
                    :state="recipientError ? false : null"
                    @input="recipientError = ''"
                />
                <b-form-invalid-feedback>{{ recipientError }}</b-form-invalid-feedback>
            </b-form-group>

            <!-- 로케일 선택 -->
            <b-form-group label="로케일" label-class="small font-weight-bold">
                <b-form-select v-model="form.locale" :options="localeOptions" size="sm" />
            </b-form-group>

            <hr />

            <!-- 발송 미리보기 -->
            <div v-if="currentPreview">
                <div
                    class="d-flex align-items-center mb-2"
                    style="cursor: pointer;"
                    @click="showPreview = !showPreview"
                >
                    <span class="mr-1 small">{{ showPreview ? '▼' : '▶' }}</span>
                    <label class="small font-weight-bold text-muted mb-0" style="cursor: pointer;">발송 미리보기</label>
                </div>
                <div v-if="showPreview" class="border rounded bg-white mb-3">
                    <div class="px-3 py-2 border-bottom bg-light">
                        <span class="small font-weight-bold">제목: </span>
                        <span class="small">{{ currentPreview.subject_text || '(제목 없음)' }}</span>
                    </div>
                    <iframe
                        :srcdoc="currentPreview.body_text"
                        sandbox
                        class="w-100 border-0"
                        style="min-height: 300px; max-height: 400px;"
                    ></iframe>
                </div>
            </div>

            <hr v-if="currentPreview" />

            <!-- 변수 입력 폼 -->
            <div v-if="filteredVariables.length === 0">
                <p class="small text-muted mb-0">발송 내용에 사용된 변수가 없습니다.</p>
            </div>
            <div v-if="filteredVariables.length > 0">
                <label class="small font-weight-bold text-muted mb-1">변수 입력 (선택)</label>
                <p class="small text-muted mb-3">발송 내용에 포함된 변수만 표시됩니다. 비워두면 변수명 그대로 표시됩니다.</p>

                <div v-for="group in filteredVariables" :key="group.key" class="mb-2">
                    <div
                        class="d-flex align-items-center py-1"
                        style="cursor: pointer;"
                        @click="toggleGroup(group.key)"
                    >
                        <span class="mr-1 small">{{ expanded[group.key] ? '▼' : '▶' }}</span>
                        <strong class="small">{{ group.label }} ({{ group.key }})</strong>
                        <b-badge variant="light" class="ml-2">{{ group.fields.length }}</b-badge>
                    </div>
                    <div v-if="expanded[group.key]" class="pl-3">
                        <b-form-group
                            v-for="field in group.fields"
                            :key="field"
                            :label="`${group.key}.${field}`"
                            label-class="small text-muted mb-0"
                            class="mb-2"
                        >
                            <b-form-input
                                v-model="variableValues[`${group.key}.${field}`]"
                                size="sm"
                                :placeholder="`${group.key}.${field}`"
                            />
                        </b-form-group>
                    </div>
                </div>
            </div>

            <hr />

            <!-- 버튼 -->
            <div class="d-flex justify-content-end">
                <b-button variant="outline-secondary" class="mr-2" @click="$bvModal.hide(modalId)">취소</b-button>
                <b-button variant="primary" :disabled="sending" @click="sendTest">
                    <b-spinner v-if="sending" small class="mr-1" />
                    테스트 발송
                </b-button>
            </div>
        </div>
    </b-modal>
</template>

<script>
import { LOCALE_OPTIONS } from '../../MailEditor/data'

const SNIPPET_REF_RE = /\{\{%\s*snippet\s+(\S+?)(?:\s+(\d+))?\s*%\}\}/g
const RESERVE_VAR_RE = /\{\{\s*reserve\s+([\w.]+)\s*\}\}/g
const MAX_SNIPPET_DEPTH = 5

export default {
    name: 'TestSendModal',
    data() {
        return {
            templateId: null,
            sending: false,
            loadingVars: false,
            showGeneration: 0,
            form: {
                recipient: '',
                locale: 'ko',
            },
            variableValues: {},
            availableVariables: [],
            contents: [],
            resolvedTexts: '',
            resolvedContents: [],
            showPreview: true,
            expanded: {},
            recipientError: '',
        }
    },
    computed: {
        modalId() {
            return `test-send-modal-${this._uid}`
        },
        localeOptions() {
            return LOCALE_OPTIONS
        },
        usedVariableKeys() {
            const keys = new Set()
            const re = new RegExp(RESERVE_VAR_RE.source, 'g')
            let m
            while ((m = re.exec(this.resolvedTexts)) !== null) {
                keys.add(m[1])
            }
            return keys
        },
        currentPreview() {
            if (!this.resolvedContents.length) return null
            const content = this.resolvedContents.find(c => c.locale === this.form.locale) || this.resolvedContents[0]
            return {
                ...content,
                body_text: (content.body_text || '').replace(/\{%[^%]*%\}/g, ''),
            }
        },
        filteredVariables() {
            if (this.usedVariableKeys.size === 0) return []
            const result = []
            this.availableVariables.forEach(g => {
                const fields = g.fields.filter(f => this.usedVariableKeys.has(`${g.key}.${f}`))
                if (fields.length > 0) {
                    result.push({ ...g, fields })
                }
            })
            return result
        },
    },
    methods: {
        async show(templateId, { availableVariables, contents } = {}) {
            const gen = ++this.showGeneration
            this.templateId = templateId
            this.contents = (contents || []).map(c => ({
                subject_text: c.subject_text || '',
                body_text: c.body_text || '',
                locale: c.locale || 'ko',
            }))
            this.$bvModal.show(this.modalId)
            this.loadingVars = true
            try {
                if (!(availableVariables && availableVariables.length > 0)) {
                    const resp = await this.$axios.get(`/mail/template/${templateId}/full`)
                    if (gen !== this.showGeneration) return
                    availableVariables = resp.data.available_variables || []
                    if (!this.contents.length && resp.data.contents) {
                        this.contents = resp.data.contents.map(c => ({
                            subject_text: c.subject_text || '',
                            body_text: c.body_text || '',
                            locale: c.locale || 'ko',
                        }))
                    }
                }
                this.availableVariables = availableVariables
                const { resolved, resolvedTexts, failedSnippets } = await this.resolveAllSnippets()
                if (gen !== this.showGeneration) return
                this.resolvedContents = resolved
                this.resolvedTexts = resolvedTexts
                if (failedSnippets.length > 0) {
                    this.$bvToast.toast(
                        `일부 스니펫을 불러오지 못했습니다: ${failedSnippets.join(', ')}`,
                        { variant: 'warning', title: '알림' }
                    )
                }
            } catch (e) {
                if (gen !== this.showGeneration) return
                this.availableVariables = availableVariables || []
                this.resolvedContents = this.contents.map(c => ({ ...c }))
                this.resolvedTexts = this.contents.map(c => (c.subject_text || '') + ' ' + (c.body_text || '')).join(' ')
                this.$bvToast.toast(
                    '스니펫 해석에 실패하여 원본 내용으로 표시됩니다.',
                    { variant: 'warning', title: '알림' }
                )
            } finally {
                if (gen === this.showGeneration) {
                    this.loadingVars = false
                    this.initVariableValues()
                }
            }
        },
        async resolveAllSnippets() {
            const failedSnippets = []
            const resolved = await Promise.all(this.contents.map(async (c) => {
                const [subject, body] = await Promise.all([
                    this.resolveSnippetsRecursive(c.subject_text || '', new Set(), 0, failedSnippets),
                    this.resolveSnippetsRecursive(c.body_text || '', new Set(), 0, failedSnippets),
                ])
                return { subject_text: subject, body_text: body, locale: c.locale }
            }))
            const resolvedTexts = resolved.map(r => r.subject_text + ' ' + r.body_text).join(' ')
            return { resolved, resolvedTexts, failedSnippets }
        },
        async resolveSnippetsRecursive(text, visited, depth, failedSnippets) {
            if (depth >= MAX_SNIPPET_DEPTH) {
                const re0 = new RegExp(SNIPPET_REF_RE.source, 'g')
                let m0
                while ((m0 = re0.exec(text)) !== null) {
                    failedSnippets.push(`${m0[1]}${m0[2] ? ' v' + m0[2] : ''} (depth 초과)`)
                }
                return text
            }
            const refs = []
            let m
            const re = new RegExp(SNIPPET_REF_RE.source, 'g')
            while ((m = re.exec(text)) !== null) {
                const refKey = `${m[1]}:${m[2] || 'latest'}`
                if (visited.has(refKey)) {
                    failedSnippets.push(`${m[1]}${m[2] ? ' v' + m[2] : ''} (순환참조)`)
                } else {
                    refs.push({ match: m[0], code: m[1], version: m[2] || null, key: refKey })
                }
            }
            if (refs.length === 0) return text
            const fetches = refs.map(async (ref) => {
                visited.add(ref.key)
                try {
                    const content = await this.fetchSnippetContent(ref.code, ref.version)
                    return { match: ref.match, content }
                } catch {
                    failedSnippets.push(`${ref.code}${ref.version ? ' v' + ref.version : ''}`)
                    return { match: ref.match, content: '' }
                }
            })
            const results = await Promise.all(fetches)
            let resolved = text
            results.forEach(r => { resolved = resolved.split(r.match).join(r.content) })
            return this.resolveSnippetsRecursive(resolved, visited, depth + 1, failedSnippets)
        },
        async fetchSnippetContent(code, version) {
            if (version) {
                const resp = await this.$axios.get(`/mail/snippet/${code}/${version}`)
                return resp.data.content || resp.data.body || ''
            }
            const listResp = await this.$axios.get('/mail/snippet', { params: { keyword: code, size: 100 } })
            const items = listResp.data.results || listResp.data.items || listResp.data || []
            const matches = items.filter(s => s.code === code)
            if (matches.length === 0) return ''
            const latest = matches.reduce((a, b) => (a.version > b.version ? a : b))
            const resp = await this.$axios.get(`/mail/snippet/${code}/${latest.version}`)
            return resp.data.content || resp.data.body || ''
        },
        initVariableValues() {
            const vals = {}
            ;(this.filteredVariables || []).forEach(g => {
                (g.fields || []).forEach(f => {
                    vals[`${g.key}.${f}`] = ''
                })
            })
            this.variableValues = vals
        },
        toggleGroup(key) {
            this.$set(this.expanded, key, !this.expanded[key])
        },
        validateEmail(email) {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
        },
        buildVariables() {
            const vars = {}
            Object.entries(this.variableValues).forEach(([key, val]) => {
                if (val !== null && val !== undefined && String(val).trim() !== '') {
                    vars[key] = val
                }
            })
            return Object.keys(vars).length > 0 ? vars : undefined
        },
        async sendTest() {
            if (this.sending) return
            if (!this.form.recipient.trim()) {
                this.recipientError = '수신자 이메일을 입력해주세요.'
                return
            }
            if (!this.validateEmail(this.form.recipient)) {
                this.recipientError = '올바른 이메일 형식을 입력해주세요.'
                return
            }
            this.recipientError = ''
            this.sending = true
            try {
                const payload = {
                    recipient: this.form.recipient,
                    locale: this.form.locale,
                }
                const variables = this.buildVariables()
                if (variables) payload.variables = variables
                const localeContent = this.contents.find(c => c.locale === this.form.locale) || this.contents[0]
                if (localeContent) {
                    payload.subject_text = localeContent.subject_text
                    payload.body_text = localeContent.body_text
                }

                const res = await this.$axios.post(
                    `/mail/template/${this.templateId}/test-send`,
                    payload
                )
                const recipient = res.data.recipient || this.form.recipient
                this.$bvToast.toast(
                    `${recipient}로 테스트 메일이 발송되었습니다.`,
                    { variant: 'success', title: '발송 완료' }
                )
                this.$bvModal.hide(this.modalId)
            } catch (e) {
                this.handleError(e)
            } finally {
                this.sending = false
            }
        },
        handleError(e) {
            const status = e.response?.status
            const data = e.response?.data
            if (status === 422) {
                const msg = (typeof data === 'object' ? (data?.error || data?.message) : data) || '입력 값을 확인해주세요.'
                this.$bvToast.toast(msg, { variant: 'warning', title: '입력 오류' })
            } else if (status === 503 || status === 501) {
                this.$bvToast.toast(
                    '테스트 발송 기능이 아직 준비되지 않았습니다.',
                    { variant: 'warning', title: '서비스 준비 중' }
                )
            } else if (!e.response) {
                this.$bvToast.toast('네트워크 연결을 확인해주세요.', { variant: 'danger', title: '연결 오류' })
            } else {
                const msg = (typeof data === 'object' ? (data?.error || data?.message) : data) || '발송에 실패했습니다.'
                this.$bvToast.toast(msg, { variant: 'danger', title: '오류' })
            }
        },
        resetForm() {
            this.showGeneration++
            this.templateId = null
            this.sending = false
            this.loadingVars = false
            this.form = { recipient: '', locale: 'ko' }
            this.variableValues = {}
            this.availableVariables = []
            this.contents = []
            this.resolvedTexts = ''
            this.resolvedContents = []
            this.showPreview = true
            this.expanded = {}
            this.recipientError = ''
        },
    },
}
</script>
